import axios from "axios";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

// Auth is carried by a Secure HttpOnly cookie set by the backend. CSRF uses a
// double-submit token stored in a readable cookie that we echo back in a header.
const client = axios.create({ baseURL: API, withCredentials: true });

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

const MUTATING = ["post", "put", "patch", "delete"];

client.interceptors.request.use((config) => {
  if (MUTATING.includes((config.method || "").toLowerCase())) {
    const csrf = getCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

const UPDATING_MSG = "DreamCRM is updating – this usually takes under a minute.";
const isOutage = (error) => {
  const r = error.response;
  if (!r) return error.code !== "ERR_CANCELED";
  if ([502, 503, 504].includes(r.status) || (r.status >= 520 && r.status <= 530)) return true;
  const d = r.data;
  return typeof d === "string" ? /cloudflare|<html/i.test(d) : typeof d?.detail === "string" && /cloudflare/i.test(d.detail);
};

client.interceptors.response.use(
  (r) => r,
  (error) => {
    if (!error.config?.skipOutage && isOutage(error)) {
      window.dispatchEvent(new CustomEvent("app:updating"));
      if (error.response) error.response.data = { detail: UPDATING_MSG };
      else error.response = { status: 0, data: { detail: UPDATING_MSG } };
      return Promise.reject(error);
    }
    if (error.response?.status === 403) {
      const d = error.response?.data?.detail;
      if (d === "PASSWORD_CHANGE_REQUIRED" || d === "MFA_SETUP_REQUIRED") {
        window.dispatchEvent(new CustomEvent("auth:refresh"));
      } else {
        toast.error(typeof d === "string" ? d : "You don't have permission to perform this action");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default client;
